import { memo } from 'react';
import { Navigate, Route, Routes } from 'react-router';

import { withSentryReactRouterRouting } from '@portal/common/services/sentry';
import lazyWithRetry from '@portal/common/utility/lazy-with-retry';

import LayoutWithSidenav from '../layout/LayoutWithSidenav';
import LayoutWithTopbar from '../layout/LayoutWithTopbar';
import AnonymousRoute from './AnonymousRoute';
import GuestOnlyRoute from './GuestOnlyRoute';
import LicensedRoute from './LicensedRoute';
import ProtectedRoute from './ProtectedRoute';
import {
    AUTH_CALLBACK_PATH,
    CONTACT_US_PATH,
    COVID_19_EXPLORER_PATH,
    DATA_EXPLORER_PATH,
    DATA_LICENSE_PATH,
    DEFINITIONS_PATH,
    DENGUE_SCENARIOS_PATH,
    EDP_EXPLORER_PATH,
    EMAIL_CONFIRMATION_PATH,
    FAVORITE_CONTENT_PATH,
    FORGOT_PASSWORD_PATH,
    HEALTH_THREATS_METRICS_PATH,
    HOME_PATH,
    IHME_STAFF_PATH,
    METHODOLOGY_PATH,
    MORBIDITY_EXPLORER_PATH,
    MY_FAVORITES_PATH,
    MY_LISTS_PATH,
    OAUTH_CONSENT_PATH,
    PERMALINK_PATH,
    PROFILE_PATH,
    PUBLIC_LISTS_PATH,
    REGISTRATION_PATH,
    RESEND_SIGN_UP_EMAIL_PATH,
    SIGN_IN_PATH,
    SIMULATION_EXPLORER_PATH,
    TEAM_PATH,
} from './paths';

const AuthCallbackScene = lazyWithRetry(() => import('../scenes/AuthCallbackScene'));
const ComorbidityExplorerScene = lazyWithRetry(() => import('../scenes/ComorbidityExplorerScene'));
const ContactUsScene = lazyWithRetry(() => import('../scenes/ContactUsScene'));
const Covid19ExplorerScene = lazyWithRetry(() => import('../scenes/Covid19ExplorerScene'));
const DataExplorerScene = lazyWithRetry(() => import('../scenes/DataExplorerScene'));
const DataLicenseScene = lazyWithRetry(() => import('../scenes/DataLicenseScene'));
const DefinitionsScene = lazyWithRetry(() => import('../scenes/DefinitionsScene'));
const DengueScenariosExplorerScene = lazyWithRetry(
    () => import('../scenes/DengueScenariosExplorerScene')
);
const EDPExplorerScene = lazyWithRetry(() => import('../scenes/EDPExplorerScene'));
const EmailConfirmationScene = lazyWithRetry(() => import('../scenes/EmailConfirmationScene'));
const ForgotPasswordScene = lazyWithRetry(() => import('../scenes/ForgotPasswordScene'));
const HealthThreatsExplorerScene = lazyWithRetry(
    () => import('../scenes/HealthThreatsExplorerScene')
);
const HomeScene = lazyWithRetry(() => import('../scenes/HomeScene'));
const MethodologyScene = lazyWithRetry(() => import('../scenes/MethodologyScene'));
const MyListScene = lazyWithRetry(() => import('../scenes/MyListScene'));
const OAuthConsentScene = lazyWithRetry(() => import('../scenes/OAuthConsentScene'));
const PermalinkScene = lazyWithRetry(() => import('../scenes/PermalinkScene'));
const ProfileScene = lazyWithRetry(() => import('../scenes/ProfileScene'));
const RegistrationScene = lazyWithRetry(() => import('../scenes/RegistrationScene'));
const ResendSignUpEmailScene = lazyWithRetry(() => import('../scenes/ResendSignUpEmailScene'));
const SignInScene = lazyWithRetry(() => import('../scenes/SignInScene'));
const SimulationExplorerScene = lazyWithRetry(() => import('../scenes/SimulationExplorerScene'));
const StaffScene = lazyWithRetry(() => import('../scenes/StaffScene'));
const TeamScene = lazyWithRetry(() => import('../scenes/TeamScene'));

const SentryRoutes = withSentryReactRouterRouting(Routes);

const AppRoutes = () => (
    <SentryRoutes>
        <Route element={<AnonymousRoute />}>
            {/* Unauthenticated User Routes */}
            <Route element={<GuestOnlyRoute />}>
                <Route path={SIGN_IN_PATH} element={<SignInScene />} />
                <Route path={REGISTRATION_PATH} element={<RegistrationScene />} />
                <Route path={RESEND_SIGN_UP_EMAIL_PATH} element={<ResendSignUpEmailScene />} />
                <Route path={FORGOT_PASSWORD_PATH} element={<ForgotPasswordScene />} />
            </Route>
            <Route path={EMAIL_CONFIRMATION_PATH} element={<EmailConfirmationScene />} />
            <Route path={AUTH_CALLBACK_PATH} element={<AuthCallbackScene />} />
        </Route>

        <Route element={<ProtectedRoute />}>
            <Route element={<LayoutWithTopbar />}>
                {/* Authenticated routes outside the app shell, e.g. consent pages opened by other apps */}
                <Route path={OAUTH_CONSENT_PATH} element={<OAuthConsentScene />} />
            </Route>

            <Route element={<LayoutWithSidenav />}>
                {/* Authenticated User Routes */}
                <Route element={<LicensedRoute />}>
                    {/* License-protected data routes */}
                    <Route path={HOME_PATH} element={<HomeScene />} />
                    <Route
                        path={FAVORITE_CONTENT_PATH + MY_LISTS_PATH + '/:listId'}
                        element={<MyListScene />}
                    />
                    <Route
                        path={FAVORITE_CONTENT_PATH + PUBLIC_LISTS_PATH + '/:listId'}
                        element={<MyListScene />}
                    />
                    <Route path={DATA_EXPLORER_PATH} element={<DataExplorerScene />} />
                    <Route path={COVID_19_EXPLORER_PATH} element={<Covid19ExplorerScene />} />
                    <Route path={MORBIDITY_EXPLORER_PATH} element={<ComorbidityExplorerScene />} />
                    <Route path={EDP_EXPLORER_PATH} element={<EDPExplorerScene />} />
                    <Route path={SIMULATION_EXPLORER_PATH} element={<SimulationExplorerScene />} />
                    <Route
                        path={HEALTH_THREATS_METRICS_PATH}
                        element={<HealthThreatsExplorerScene />}
                    />
                    <Route
                        path={DENGUE_SCENARIOS_PATH}
                        element={<DengueScenariosExplorerScene />}
                    />
                    <Route path={PERMALINK_PATH + '/:hash'} element={<PermalinkScene />} />
                </Route>
                {/* Non-license-protected routes */}
                <Route path={DEFINITIONS_PATH} element={<DefinitionsScene />} />
                <Route path={METHODOLOGY_PATH} element={<MethodologyScene />} />
                <Route path={IHME_STAFF_PATH} element={<StaffScene />} />
                <Route path={CONTACT_US_PATH} element={<ContactUsScene />} />
                <Route path={DATA_LICENSE_PATH} element={<DataLicenseScene />} />
                <Route path={PROFILE_PATH + ':tabId?'} element={<ProfileScene />} />
                <Route path={TEAM_PATH} element={<TeamScene />} />
                <Route
                    path={FAVORITE_CONTENT_PATH}
                    element={<Navigate to={MY_FAVORITES_PATH} replace />}
                />
                <Route path="*" element={<Navigate to={HOME_PATH} replace />} />
            </Route>
        </Route>
    </SentryRoutes>
);

export default memo(AppRoutes);
